import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { clx } from "../../../../helpers/components";
import { Reveal } from "../../../shared/Reveal";
import { useEntryAnimation } from "../../../shared/FramerMotionAnimations";
import style from "./style.module.scss";

// Valeurs (données, identiques FR/EN) ; libellés via i18n.
// number = cible du compteur, suffix = texte gardé (+, h, %).
const ITEMS = [
    { number: 500, suffix: "+", label: "received" },
    { number: 48, suffix: "h", label: "responseTime" },
    { number: 92, suffix: "%", label: "returnRate" },
    { number: 30, suffix: "+", label: "partnerships" },
];

const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Compteur animé 0 → `to`, déclenché quand `active` passe à true.
 * easeOutCubic, via requestAnimationFrame. Garde le suffixe (+, h, %).
 */
function CountUp({ to, suffix = "", duration = 1.8, active }) {
    const [val, setVal] = React.useState(0);

    React.useEffect(() => {
        if (!active) return undefined;
        if (prefersReducedMotion()) {
            setVal(to);
            return undefined;
        }
        let raf;
        let startTs = null;
        const step = (ts) => {
            if (startTs === null) startTs = ts;
            const p = Math.min(1, (ts - startTs) / (duration * 1000));
            const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
            setVal(Math.round(eased * to));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [active, to, duration]);

    return (
        <>
            {val}
            {suffix}
        </>
    );
}

/**
 * Barre de statistiques « Story » (Figma node 10030:7206).
 * Barre teal arrondie : 4 blocs (valeur animée + libellé) séparés par des
 * traits blancs verticaux. Libellés via i18n (base configurable).
 */
export function StatsBar({ base = "partners" } = {}) {
    const { t: t0 } = useTranslation("translations");
    const t = (key, opts) =>
        t0(typeof key === "string" ? key.replace(/^partners\./, `${base}.`) : key, opts);

    // Déclenche le comptage quand la barre entre à l'écran.
    const [ref, setRef] = React.useState(null);
    const { hasAnimated } = useEntryAnimation(ref);

    return (
        <Reveal className={clx("container", style.wrapper)}>
            <div className={style.bar} ref={setRef}>
                {ITEMS.map((item, i) => (
                    <Fragment key={item.label}>
                        {i > 0 && <span className={style.divider} aria-hidden="true" />}
                        <div className={style.item}>
                            <span className={style.value}>
                                <CountUp
                                    to={item.number}
                                    suffix={item.suffix}
                                    active={hasAnimated}
                                />
                            </span>
                            <span className={style.label}>
                                {t(`partners.stats.${item.label}`)}
                            </span>
                        </div>
                    </Fragment>
                ))}
            </div>
        </Reveal>
    );
}
